import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

// Zustand store for canvas updates
import useCanvasStore from "../../stores/canvasStore";

// Redux actions (only for non-canvas features like Google Drive)
import {
    fetchOrSyncDriveFiles,
    syncDriveFiles,
    createDriveFolder,
} from "../../redux/actions/driveFiles.action";

// API service
import * as itemService from "../../services/itemDetails.service";

/**
 * Custom hook to manage item details modal state and operations
 */
export const useItemDetails = ({
    isOpen,
    itemData,
    itemInfo,
    itemType,
    depth,
    handleClose,
    updateNameHandler,
    setItemData,
    on_delete,
    fetchItemDetails,
    href,
}) => {
    // Local state
    const [folderName, setFolderName] = useState(itemData.name);
    const [linkVal, setLinkVal] = useState(href);
    const [filterComment, setFilterComment] = useState(false);
    const [isArchive, setIsArchive] = useState(itemData.is_archived);
    const [openTag, setOpenTag] = useState(false);
    const [tags, setTags] = useState([]);
    const [recentTags, setRecentTags] = useState([]);
    const [comment, setComment] = useState("");
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [starLevel, setStarLevel] = useState(itemData.star_level || 0);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [savingComment, setSavingComment] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isLinkEditing, setIsLinkEditing] = useState(false);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaType, setMediaType] = useState("");
    const [isCreatingDescriptionGoogleDoc, setIsCreatingDescriptionGoogleDoc] = useState(false);
    const [isDocLoading, setIsDocLoading] = useState(true);

    const dispatch = useDispatch();

    // Zustand store methods for canvas updates
    const updateItemOptimistic = useCanvasStore(s => s.updateItemOptimistic);
    const fetchCanvas = useCanvasStore(s => s.fetchCanvas);
    const currentFolder = useCanvasStore(s => s.currentFolder);

    // Google Drive state from Redux
    const driveData = useSelector(
        (state) => state.driveFiles.byFolderId[itemData?.id] || {}
    );
    const {
        driveFiles = [],
        gdriveFolderId,
        lastSyncedAt,
        syncing: driveSyncing = false,
        creating: creatingDriveFolder = false,
    } = driveData;

    // Derived values
    const itemDetails = itemInfo?.folder || itemInfo?.link;
    const itemComments = itemInfo?.folder_comments || itemInfo?.link_comments;
    const isItemType = itemType === "folder" ? itemInfo?.folder : itemInfo?.link;
    const hasDescriptionGdoc = isItemType?.descriptionGdocId?.length > 0;
    const descriptionGdocUrl = `https://docs.google.com/document/d/${isItemType?.descriptionGdocId}`;

    // Helper to update item info
    const handleDetailsChange = useCallback((newData) => setItemData(newData), [setItemData]);

    // Event handlers
    const handleLinkChange = (event) => setLinkVal(event.target.value);
    const handleLinkClick = () => setIsLinkEditing(true);
    const handleNameClick = () => setIsEditing(true);
    const handleNameChange = (event) => setFolderName(event.target.value);
    const handleNameBlur = () => setIsEditing(false);
    const handleCommentChange = (e) => setComment(e.target.value);
    const handleFilterCommentChange = () => setFilterComment(!filterComment);
    const handleDeleteFolder = () => setShowDeleteConfirmation(true);

    // Tag dialog handlers
    const handleTagDialogOpen = async () => {
        setOpenTag(true);
        try {
            const tags = await itemService.fetchRecentTags();
            setRecentTags(tags);
        } catch (error) {
            console.log("Error fetching recent tags:", error);
        }
    };

    const handleTagDialogClose = () => setOpenTag(false);

    // File upload handler
    const onFileUpload = (file_, type_) => {
        setMediaFile(file_);
        setMediaType(type_);
    };

    // Comment modification handler
    const handleCommentModify = (commentId, isDeleted, newComment) => {
        const commentsKey = itemType === "folder" ? "folder_comments" : "link_comments";
        
        if (isDeleted) {
            handleDetailsChange({
                ...itemInfo,
                [commentsKey]: itemComments.filter((comment) => comment.id !== commentId),
            });
        } else {
            const existingIndex = itemComments.findIndex((comment) => comment.id === commentId);
            const newData = { ...itemInfo };
            newData[commentsKey][existingIndex].comment = newComment;
            handleDetailsChange(newData);
        }
    };

    // Rename folder/link
    const renameFolder = async (id, name, currentDepth) => {
        try {
            const response = await itemService.renameItem(id, name);
            const updatedItem = itemType === "folder" ? response.folder : response.link;
            const itemKey = itemType === "folder" ? "folder" : "link";
            const commentsKey = itemType === "folder" ? "folder_comments" : "link_comments";

            handleDetailsChange({
                ...itemInfo,
                [itemKey]: { ...itemInfo[itemKey], name },
                [commentsKey]: [...itemInfo[commentsKey], response.comment],
            });

            toast.success("Item Renamed Successfully.");

            if (currentDepth !== -1) {
                // Use Zustand for canvas updates
                updateItemOptimistic(id, currentDepth, { name: updatedItem.name });
            } else {
                updateNameHandler(id, name);
            }
        } catch (error) {
            toast.error("Error Renaming Folder");
        }
        setIsEditing(false);
    };

    const updateFolderName = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setIsEditing(false);
            // Reset folder name to original
            setFolderName(itemData.name);
            return;
        }
        if (event.key === "Enter" && folderName.trim() !== "") {
            if (folderName.trim() === itemData.name) {
                setIsEditing(false);
                return;
            }
            renameFolder(itemData.id, folderName, depth);
        }
    };

    // Update link URL
    const updateLink = async (id, link, currentDepth) => {
        try {
            const response = await itemService.updateLinkUrl(id, link);
            const link_data = response.link;

            setItemData({
                ...itemInfo,
                link: { ...itemInfo.link, link },
                link_comments: [...itemInfo.link_comments, response.comment],
            });

            toast.success("Link Updated Successfully.");

            if (currentDepth !== -1) {
                // Use Zustand for canvas updates
                updateItemOptimistic(id, currentDepth, { link: link_data.link, name: link_data.name });
            } else {
                updateNameHandler(id, itemData.name, linkVal);
            }
        } catch (error) {
            toast.error("Error Updating Link");
        }
        setIsLinkEditing(false);
    };

    const updateLinkValue = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setIsLinkEditing(false);
            // Reset link value to original
            setLinkVal(itemDetails.link);
            return;
        }
        if (event.key === "Enter" && linkVal.trim() !== "") {
            if (linkVal.trim() === itemDetails.link) {
                setIsLinkEditing(false);
                return;
            }
            try {
                new URL(linkVal);
            } catch (error) {
                toast.error("Please enter a valid Link.");
                return;
            }
            updateLink(itemData.id, linkVal, depth);
        }
    };

    // Share link
    const handleShareLink = () => {
        const url = `${window.location.origin}/folder/${itemData.id}?archiveStatus=${Cookies.get("archiveStatus")}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success("Link Copied to Clipboard.");
        });
    };

    // Search within folder
    const handleSearchWithin = () => {
        const searchUrl = `${window.location.origin}/search?ancestor_id=${itemData.id}`;
        window.open(searchUrl, "_blank");
    };

    // Archive toggle
    const handleArchiveClick = async () => {
        const newIsArchive = !isArchive;
        setIsArchive(newIsArchive);
        try {
            await itemService.changeArchiveStatus(itemData.id, newIsArchive);
            toast.success(`Archive Status set ${newIsArchive} successfully`);

            const itemKey = itemType === "folder" ? "folder" : "link";
            handleDetailsChange({
                ...itemInfo,
                [itemKey]: { ...itemInfo[itemKey], is_archived: newIsArchive },
            });

            if (depth !== -1) {
                // Use Zustand for canvas updates
                updateItemOptimistic(itemData.id, depth, { is_archived: newIsArchive });
                // Re-fetch canvas if archive filter is active
                const archiveStatus = useCanvasStore.getState().archiveStatus;
                if (archiveStatus !== 'all') {
                    await fetchCanvas(currentFolder?.id);
                }
            } else if (itemType === "folder") {
                updateNameHandler(itemData.id, folderName, null, null, newIsArchive);
            }
        } catch (error) {
            toast.error("Error Updating Status");
        }
    };

    // Star toggle
    const handleStarClick = useCallback(async () => {
        try {
            const item = itemType === "folder" ? itemInfo.folder : itemInfo.link;
            const toggleStarLevel = useCanvasStore.getState().toggleStarLevel;
            const newStarLevel = await toggleStarLevel(item, depth);
            setStarLevel(newStarLevel);
            if (depth === -1) {
                updateNameHandler(itemData.id, folderName, null, newStarLevel);
            }
        } catch (error) {
            console.log(error);
        }
    }, [itemType, itemInfo, depth, itemData.id, folderName, updateNameHandler]);

    // Bookmark toggle
    const handleBookmarkClick = async () => {
        const newIsBookmarked = !isBookmarked;
        setIsBookmarked(newIsBookmarked);
        try {
            await itemService.toggleBookmark(itemData.id);
            toast.success(newIsBookmarked ? "Item Bookmarked" : "Bookmark Removed");
            handleDetailsChange({ ...itemInfo, is_bookmarked: newIsBookmarked });
        } catch (error) {
            console.log(error);
            setIsBookmarked(!newIsBookmarked);
            toast.error("Error Updating Bookmark");
        }
    };

    // Status change
    const handleStatusChange = async (value) => {
        try {
            const response = await itemService.changeFolderStatus(itemData.id, value);
            toast.success("Status Updated Successfully.");

            handleDetailsChange({
                ...itemInfo,
                folder: { ...itemInfo.folder, status: value },
                state_machine: response.state_machine,
            });

            if (depth !== -1) {
                // Use Zustand for canvas updates
                updateItemOptimistic(itemData.id, depth, { status: value });
            } else {
                updateNameHandler(itemData.id, folderName, value);
            }
        } catch (error) {
            console.log(error);
            toast.error("Error Updating Status");
        }
    };

    // Assignee selection
    const handleAssigneeSelect = (employees, comment) => {
        const itemKey = itemType === "folder" ? "folder" : "link";
        const commentsKey = itemType === "folder" ? "folder_comments" : "link_comments";

        handleDetailsChange({
            ...itemInfo,
            [itemKey]: { ...itemInfo[itemKey], employees },
            [commentsKey]: [...itemInfo[commentsKey], comment],
        });

        if (depth !== -1) {
            // Use Zustand for canvas updates
            updateItemOptimistic(itemData.id, depth, { employees });
        }
    };

    // Delete confirmation
    const handleConfirmDelete = async () => {
        try {
            await itemService.deleteItem(itemData.id, itemType);
            toast.success("Item Deleted Successfully.");
            handleClose();

            if (depth !== -1) {
                // Use Zustand for canvas updates - just remove from UI (server already deleted)
                const items = useCanvasStore.getState().items;
                const itemsAtDepth = items[depth] || [];
                useCanvasStore.setState({
                    items: {
                        ...items,
                        [depth]: itemsAtDepth.filter(i => i.id !== itemData.id)
                    }
                });
            } else {
                on_delete(itemData.id);
            }
        } catch (error) {
            toast.error("Error Deleting Folder");
        } finally {
            setShowDeleteConfirmation(false);
        }
    };

    // Aggregator data change
    const onAggregatorDataChange = (data) => {
        const existingIndex = itemInfo.aggregator_data.findIndex((entry) => entry.key === data.key);
        const newFolderData = { ...itemInfo };
        if (existingIndex !== -1) {
            newFolderData.aggregator_data[existingIndex] = data;
        } else {
            newFolderData.aggregator_data = [...itemInfo.aggregator_data, data];
        }
        handleDetailsChange(newFolderData);
    };

    // Filter comments
    const getFilteredComments = () => {
        if (!itemComments) return [];
        return itemComments.filter((comment) => {
            if (!filterComment) return true;
            return comment.is_comment;
        });
    };

    // Save comment
    const saveComment = async (event) => {
        if (event.key === "Enter" && comment.trim() !== "") {
            try {
                setSavingComment(true);
                const response = await itemService.addComment(itemData.id, comment, mediaType, mediaFile);
                toast.success("Comment Saved Successfully.");
                setSavingComment(false);
                setComment("");
                setMediaType("");
                setMediaFile(null);

                const commentsKey = itemType === "folder" ? "folder_comments" : "link_comments";
                handleDetailsChange({
                    ...itemInfo,
                    [commentsKey]: [...itemInfo[commentsKey], response],
                });
            } catch (error) {
                toast.error("Error Saving Comment.");
                setSavingComment(false);
            }
        } else if (event.key === "Enter" && comment.trim() === "") {
            toast.error("Comment field can not be blank");
        }
    };

    // Create description Google Doc
    const handleCreateDescriptionGoogleDoc = async () => {
        try {
            setIsCreatingDescriptionGoogleDoc(true);
            await itemService.createDescriptionGoogleDoc(itemData.id);
            await fetchItemDetails();
            setIsCreatingDescriptionGoogleDoc(false);
        } catch (error) {
            setIsCreatingDescriptionGoogleDoc(false);
            toast.error("Error Creating Description Google Doc");
        }
    };

    const handleOpenInNewTab = () => window.open(descriptionGdocUrl, "_blank");

    // Google Drive handlers
    const handleCreateDriveFolder = (folderId) => dispatch(createDriveFolder(folderId));
    const handleSyncDriveFiles = (folderId) => dispatch(syncDriveFiles(folderId, { force: true }));

    // Tag save handler
    const handleTagSave = (updatedTags) => {
        setTags(updatedTags);
        const itemKey = itemType === "folder" ? "folder" : "link";

        handleDetailsChange({
            ...itemInfo,
            [itemKey]: { ...itemInfo[itemKey], tags: updatedTags },
        });

        if (depth !== -1) {
            // Use Zustand for canvas updates
            updateItemOptimistic(itemData.id, depth, { tags: updatedTags });
        } else {
            updateNameHandler(itemData.id, folderName, null, null, null, updatedTags);
        }
    };

    // Effects
    useEffect(() => {
        if (isOpen && itemInfo?.folder && itemType === "folder" && itemInfo.folder.id) {
            dispatch(fetchOrSyncDriveFiles(itemInfo.folder.id));
        }
    }, [isOpen, itemInfo?.folder?.id, itemType, dispatch]);

    useEffect(() => {
        if (itemInfo?.is_bookmarked !== undefined) {
            setIsBookmarked(itemInfo.is_bookmarked);
        }
    }, [itemInfo?.is_bookmarked]);

    useEffect(() => {
        if (itemType === "folder" && itemInfo?.folder?.tags) {
            setTags(itemInfo.folder.tags);
        } else if (itemType === "link" && itemInfo?.link?.tags) {
            setTags(itemInfo.link.tags);
        }
    }, [itemInfo?.folder?.tags, itemInfo?.link?.tags, itemType]);

    useEffect(() => {
        if (isOpen) {
            // setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [isOpen]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.repeat) return;
            if (isOpen) {
                // Only handle Ctrl+Q for star toggle, let other keys (like Escape) pass through
                if ((event.ctrlKey || event.metaKey) && event.key === "q") {
                    event.preventDefault();
                    event.stopPropagation();
                    handleStarClick();
                }
            }
        };

        if (isOpen) {
            window.addEventListener("keydown", handleKeyPress, true);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyPress, true);
        };
    }, [isOpen, handleStarClick]);

    return {
        // State
        folderName,
        linkVal,
        filterComment,
        isArchive,
        openTag,
        tags,
        recentTags,
        comment,
        showDeleteConfirmation,
        starLevel,
        isBookmarked,
        savingComment,
        isEditing,
        isLinkEditing,
        isCreatingDescriptionGoogleDoc,
        isDocLoading,
        setIsDocLoading,
        setShowDeleteConfirmation,
        setRecentTags,

        // Google Drive state
        driveFiles,
        gdriveFolderId,
        lastSyncedAt,
        driveSyncing,
        creatingDriveFolder,

        // Derived values
        itemDetails,
        itemComments,
        hasDescriptionGdoc,
        descriptionGdocUrl,

        // Handlers
        handleLinkChange,
        handleLinkClick,
        handleNameClick,
        handleNameChange,
        handleNameBlur,
        handleCommentChange,
        handleFilterCommentChange,
        handleDeleteFolder,
        handleTagDialogOpen,
        handleTagDialogClose,
        onFileUpload,
        handleCommentModify,
        updateFolderName,
        updateLinkValue,
        handleShareLink,
        handleSearchWithin,
        handleArchiveClick,
        handleStarClick,
        handleBookmarkClick,
        handleStatusChange,
        handleAssigneeSelect,
        handleConfirmDelete,
        onAggregatorDataChange,
        getFilteredComments,
        saveComment,
        handleCreateDescriptionGoogleDoc,
        handleOpenInNewTab,
        handleCreateDriveFolder,
        handleSyncDriveFiles,
        handleTagSave,
    };
};
